import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatCard, SectionCard, StatusBadge, Empty } from "@/components/shared";
import { inr, mt, fmtDateTime } from "@/lib/format";
import { FileText, Receipt, PackageCheck, Truck, Plus, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["RFQ", "Quotes", "Orders", "Dispatches"];

export default function Distributor() {
  const [roll, setRoll] = useState(null);
  const [step, setStep] = useState("RFQ");
  const [dists, setDists] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => {
    api.get("/distributor/rollups").then((r) => setRoll(r.data));
    api.get("/distributor/rfqs").then((r) => setRfqs(r.data));
    api.get("/distributor/quotes").then((r) => setQuotes(r.data));
    api.get("/distributor/orders").then((r) => setOrders(r.data));
    api.get("/distributor/dispatches").then((r) => setDispatches(r.data));
  };
  useEffect(() => { load(); api.get("/distributor/list").then((r) => setDists(r.data)); }, []);

  return (
    <div>
      <PageHeader title="Distributor Portal" subtitle="RFQ → Quote → Order → Dispatch with MT rollups. Dispatch accrues pending loyalty points." testid="distributor-rfq-order-flow" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatCard label="Open RFQs" value={roll?.rfq_count ?? "—"} accent="#2563EB" icon={FileText} testid="dist-rfq" />
        <StatCard label="Quoted MT" value={roll ? mt(roll.quoted_mt) : "—"} sub={roll ? inr(roll.quoted_value) : ""} accent="#0891B2" icon={Receipt} testid="dist-quoted" />
        <StatCard label="Ordered MT" value={roll ? mt(roll.ordered_mt) : "—"} sub={roll ? inr(roll.ordered_value) : ""} accent="#7C3AED" icon={PackageCheck} testid="dist-ordered" />
        <StatCard label="Supplied MT" value={roll ? mt(roll.supplied_mt) : "—"} sub={roll ? inr(roll.supplied_value) : ""} accent="#059669" icon={Truck} testid="dist-supplied" />
      </div>

      <div className="flex items-center gap-2 mb-5 overflow-x-auto">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <button data-testid={`dstep-${s.toLowerCase()}`} onClick={() => setStep(s)} className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${step === s ? "text-white copper-glow" : "bg-white border border-slate-200 text-slate-600"}`} style={step === s ? { background: "var(--copper)" } : {}}>{s}</button>
            {i < STEPS.length - 1 && <ChevronRight size={16} className="text-slate-300" />}
          </React.Fragment>
        ))}
        <div className="ml-auto">
          {step === "RFQ" && <ActionBtn label="New RFQ" onClick={() => setDialog("rfq")} />}
        </div>
      </div>

      {step === "RFQ" && (
        <SectionCard className="!p-0">
          <Tbl head={["RFQ No.", "Distributor", "Project", "MT", "Status", "Action"]} empty={!rfqs.length}>
            {rfqs.map((r) => (
              <tr key={r.id} className="border-b border-slate-50">
                <td className="px-5 py-3 font-mono text-xs">{r.rfq_no}</td><td className="px-5 py-3">{r.distributor_name}</td>
                <td className="px-5 py-3">{r.project_name}</td><td className="px-5 py-3 font-mono">{mt(r.total_mt)}</td>
                <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-5 py-3">{r.status === "open" && <button className="text-xs text-[var(--copper)] font-semibold" onClick={() => setDialog({ type: "quote", rfq: r })}>Create Quote →</button>}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}
      {step === "Quotes" && (
        <SectionCard className="!p-0">
          <Tbl head={["Quote No.", "Distributor", "Project", "MT", "Value", "Status", "Action"]} empty={!quotes.length}>
            {quotes.map((q) => (
              <tr key={q.id} className="border-b border-slate-50">
                <td className="px-5 py-3 font-mono text-xs">{q.quote_no}</td><td className="px-5 py-3">{q.distributor_name}</td>
                <td className="px-5 py-3">{q.project_name}</td><td className="px-5 py-3 font-mono">{mt(q.total_mt)}</td>
                <td className="px-5 py-3 font-mono">{inr(q.total_value)}</td><td className="px-5 py-3"><StatusBadge status={q.status} /></td>
                <td className="px-5 py-3">{q.status === "quoted" && <button className="text-xs text-[var(--copper)] font-semibold" onClick={() => setDialog({ type: "order", quote: q })}>Convert to Order →</button>}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}
      {step === "Orders" && (
        <SectionCard className="!p-0">
          <Tbl head={["Order No.", "PO", "Distributor", "Project", "MT", "Value", "Status", "Action"]} empty={!orders.length}>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-slate-50">
                <td className="px-5 py-3 font-mono text-xs">{o.order_no}</td><td className="px-5 py-3 font-mono text-xs">{o.po_number}</td>
                <td className="px-5 py-3">{o.distributor_name}</td><td className="px-5 py-3">{o.project_name}</td>
                <td className="px-5 py-3 font-mono">{mt(o.total_mt)}</td><td className="px-5 py-3 font-mono">{inr(o.total_value)}</td>
                <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-5 py-3">{o.status === "ordered" && <button className="text-xs text-[var(--copper)] font-semibold" onClick={() => setDialog({ type: "dispatch", order: o })}>Dispatch →</button>}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}
      {step === "Dispatches" && (
        <SectionCard className="!p-0">
          <Tbl head={["Dispatch No.", "Distributor", "Project", "MT", "Value", "LR No.", "Vehicle", "Date"]} empty={!dispatches.length}>
            {dispatches.map((d) => (
              <tr key={d.id} className="border-b border-slate-50">
                <td className="px-5 py-3 font-mono text-xs">{d.dispatch_no}</td><td className="px-5 py-3">{d.distributor_name}</td>
                <td className="px-5 py-3">{d.project_name}</td><td className="px-5 py-3 font-mono">{mt(d.total_mt)}</td>
                <td className="px-5 py-3 font-mono">{inr(d.total_value)}</td><td className="px-5 py-3 font-mono text-xs">{d.lr_number}</td>
                <td className="px-5 py-3 text-xs">{d.vehicle}</td><td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(d.created_at)}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}

      {dialog === "rfq" && <RfqDialog dists={dists} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); toast.success("RFQ created"); }} />}
      {dialog?.type === "quote" && <QuoteDialog rfq={dialog.rfq} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); setStep("Quotes"); toast.success("Quote created"); }} />}
      {dialog?.type === "order" && <OrderDialog quote={dialog.quote} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); setStep("Orders"); toast.success("Order created"); }} />}
      {dialog?.type === "dispatch" && <DispatchDialog order={dialog.order} onClose={() => setDialog(null)} onDone={(res) => { setDialog(null); load(); setStep("Dispatches"); toast.success(res.points_accrued ? `Dispatched · ${res.points_accrued.points} pending points accrued` : "Dispatched"); }} />}
    </div>
  );
}

function ActionBtn({ label, onClick }) {
  return <button data-testid="new-rfq-btn" onClick={onClick} className="flex items-center gap-1.5 text-white font-semibold px-4 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}><Plus size={16} /> {label}</button>;
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-900">{title}</h3><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

function RfqDialog({ dists, onClose, onDone }) {
  const [distributor_id, setDist] = useState(dists[0]?.id || "");
  const [project_name, setPn] = useState("");
  const [qty, setQty] = useState(5);
  const [rate, setRate] = useState(850000);
  const submit = async () => {
    try { await api.post("/distributor/rfqs", { distributor_id, project_name, items: [{ sku: "Cu-Tube-1/2in", qty_mt: Number(qty), rate: Number(rate) }] }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title="New RFQ" onClose={onClose}>
      <div className="space-y-3">
        <select value={distributor_id} onChange={(e) => setDist(e.target.value)} className="inp2" data-testid="rfq-distributor">{dists.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
        <input value={project_name} onChange={(e) => setPn(e.target.value)} placeholder="Project name" className="inp2" data-testid="rfq-project" />
        <div className="grid grid-cols-2 gap-3">
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty MT" className="inp2" data-testid="rfq-qty" />
          <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Rate/MT" className="inp2" />
        </div>
        <button data-testid="rfq-submit" onClick={submit} className="w-full text-white font-semibold py-2 rounded-lg" style={{ background: "var(--copper)" }}>Create RFQ</button>
      </div>
      <Sty />
    </Modal>
  );
}

function QuoteDialog({ rfq, onClose, onDone }) {
  const it = rfq.items[0];
  const [qty, setQty] = useState(it.qty_mt);
  const [rate, setRate] = useState(it.rate || 850000);
  const submit = async () => {
    try { await api.post("/distributor/quote", { rfq_id: rfq.id, items: [{ sku: it.sku, qty_mt: Number(qty), rate: Number(rate) }] }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={`Quote for ${rfq.rfq_no}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">{rfq.project_name} · {rfq.distributor_name}</p>
        <div className="grid grid-cols-2 gap-3">
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="inp2" data-testid="quote-qty" />
          <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="inp2" data-testid="quote-rate" />
        </div>
        <p className="text-sm font-mono">Total: {inr(qty * rate)}</p>
        <button data-testid="quote-submit" onClick={submit} className="w-full text-white font-semibold py-2 rounded-lg" style={{ background: "var(--copper)" }}>Send Quote</button>
      </div>
      <Sty />
    </Modal>
  );
}

function OrderDialog({ quote, onClose, onDone }) {
  const [po, setPo] = useState(`PO/2026/${Math.floor(Math.random() * 9000 + 1000)}`);
  const submit = async () => {
    try { await api.post("/distributor/order", { quote_id: quote.id, po_number: po }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={`Order from ${quote.quote_no}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">{quote.project_name} · {mt(quote.total_mt)} · {inr(quote.total_value)}</p>
        <input value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO Number" className="inp2" data-testid="order-po" />
        <button data-testid="order-submit" onClick={submit} className="w-full text-white font-semibold py-2 rounded-lg" style={{ background: "var(--copper)" }}>Confirm Order</button>
      </div>
      <Sty />
    </Modal>
  );
}

function DispatchDialog({ order, onClose, onDone }) {
  const [dispatched_mt, setMt] = useState(order.total_mt);
  const [lr, setLr] = useState(`LR${Math.floor(Math.random() * 90000 + 10000)}`);
  const [vehicle, setVeh] = useState("");
  const submit = async () => {
    try { const { data } = await api.post("/distributor/dispatch", { order_id: order.id, dispatched_mt: Number(dispatched_mt), lr_number: lr, vehicle }); onDone(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={`Dispatch · ${order.order_no}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">{order.project_name} · ordered {mt(order.total_mt)}</p>
        <input type="number" value={dispatched_mt} onChange={(e) => setMt(e.target.value)} placeholder="Dispatched MT" className="inp2" data-testid="dispatch-mt" />
        <div className="grid grid-cols-2 gap-3">
          <input value={lr} onChange={(e) => setLr(e.target.value)} placeholder="LR No." className="inp2" data-testid="dispatch-lr" />
          <input value={vehicle} onChange={(e) => setVeh(e.target.value)} placeholder="Vehicle" className="inp2" />
        </div>
        <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">Dispatch accrues <b>pending</b> loyalty points to the first verified recommender (approved by Rewards Ops).</p>
        <button data-testid="dispatch-submit" onClick={submit} className="w-full text-white font-semibold py-2 rounded-lg" style={{ background: "var(--copper)" }}>Record Dispatch</button>
      </div>
      <Sty />
    </Modal>
  );
}

function Sty() { return <style>{`.inp2{width:100%;border:1px solid #CBD5E1;border-radius:8px;padding:8px 10px;font-size:14px;background:#fff}.inp2:focus{outline:none;border-color:var(--copper)}`}</style>; }

function Tbl({ head, children, empty }) {
  return (<div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">{head.map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr></thead><tbody>{children}</tbody></table>{empty && <Empty />}</div>);
}
